import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Collapse, Flex, Stack, Text } from "@chakra-ui/react";
import { CodeChallengeLoading } from "./CodeChallenge";
import { FaChevronRight } from "react-icons/fa";
import { FiAlertCircle, FiCheckCircle, FiZap } from "react-icons/fi";
import { TaskContent, TaskEvent, TaskWrapper, useTask } from "./Task";
import { useConfetti } from "@utils";

type ResultObject = Map<string, boolean | ResultObject>;

const Result: React.FC<{
  label: string;
  results: ResultObject | boolean;
}> = ({ results, label }) => {
  const isPassingAllSubResults = useCallback(
    (resultObj: ResultObject): boolean => {
      return [...resultObj.values()].every((result) => {
        if (typeof result === "boolean") return result;
        return isPassingAllSubResults(result);
      });
    },
    []
  );

  const passed =
    typeof results === "boolean" ? results : isPassingAllSubResults(results);

  const [isOpen, setIsOpen] = useState(!passed);

  useEffect(() => {
    setIsOpen(!passed);
  }, [passed]);

  const hasSubTests = typeof results !== "boolean";

  return (
    <Box>
      <Flex
        align="center"
        fontSize="md"
        color={passed ? "success" : "failure"}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
        cursor={hasSubTests ? "pointer" : "default"}
      >
        {hasSubTests ? (
          <FaChevronRight
            style={{
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
              visibility: hasSubTests ? "visible" : "hidden",
              marginRight: 6,
            }}
          />
        ) : (
          <Box mr="1">{passed ? <FiCheckCircle /> : <FiAlertCircle />}</Box>
        )}
        <span>
          <TaskContent>{label}</TaskContent>
        </span>
      </Flex>
      {typeof results !== "boolean" && (
        <Box pos="relative">
          <Collapse in={isOpen} animateOpacity>
            <Stack ml="4" mt="2">
              {[...results.entries()].map(([label, results]) => (
                <Result key={label} label={label} results={results} />
              ))}
            </Stack>
          </Collapse>
          <Box
            pos="absolute"
            top={0}
            bottom={0}
            left="7px"
            width="1px"
            bg={passed ? "success" : "failure"}
            opacity="0.5"
          />
          <Box
            pos="absolute"
            top={0}
            bottom={0}
            left={0}
            width="16px"
            cursor="pointer"
            onClick={() => setIsOpen(false)}
          />
        </Box>
      )}
    </Box>
  );
};

export const Capsule: React.FC<{
  challenge: string;
  description: string;
  taskId: string;
  version?: string;
  size?: string;
}> = ({ challenge, version, description, taskId, size }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [containerRef, confetti] = useConfetti<HTMLDivElement>({
    position: "bottom-left",
  });
  const { loadingUser, isTaskComplete, saveTask, task } = useTask(taskId);
  const [complete, setComplete] = useState(isTaskComplete);
  useEffect(() => {
    setComplete(isTaskComplete);
  }, [isTaskComplete]);
  const [isCapsuleReady, setIsCapsuleReady] = useState(false);

  const [results, setResults] = useState<Array<{
    path: string[];
    passed: boolean;
  }> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const height = size === "lg" ? "900px" : "400px";

  useEffect(() => {
    const taskValueStr = task?.value ?? "{}";
    let userCode: Record<string, string> = {};
    try {
      userCode = JSON.parse(taskValueStr);
    } catch (e) {
      // Old tasks were stored as a string and will fail to parse
    }
    frameRef.current?.contentWindow?.postMessage(
      {
        type: "initialize",
        userCode,
        completed: isTaskComplete,
      },
      "https://capsule.apollographql.com"
    );
  }, [loadingUser, isTaskComplete, isCapsuleReady, task?.value]);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      if (event.data.type === "results") {
        setResults(event.data.results);
        setError(null);
        const isPassed =
          (event.data.results as typeof results)?.every(
            (result) => result.passed
          ) ?? false;
        saveTask(
          isPassed ? new Date() : null,
          JSON.stringify(event.data.userCode)
        );
        if (isPassed) {
          confetti();
        }
      }
      if (event.data.type === "error") {
        setResults(null);
        setError(event.data.error);
        saveTask(null, JSON.stringify(event.data.userCode));
      }
      if (event.data.type === "ready") {
        setIsCapsuleReady(true);
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [confetti, saveTask]);

  useEffect(() => {
    if (!results) {
      setComplete(false);
      return;
    }
    setComplete(results.every((result) => result.passed));
  }, [results]);

  const resultsObj = useMemo(() => {
    if (!results) return new Map() as ResultObject;
    return results.reduce((acc, result) => {
      let map = acc;
      result.path.forEach((key, index) => {
        if (index === result.path.length - 1) {
          map.set(key, result.passed);
        } else {
          let subMap = map.get(key);
          if (subMap === undefined) {
            subMap = new Map();
            map.set(key, subMap);
          }
          map = subMap as ResultObject;
        }
      });
      return acc;
    }, new Map() as ResultObject);
  }, [results]);

  const v = version ?? "latest";
  return (
    <TaskWrapper
      icon={FiZap}
      title="Code Challenge!"
      isComplete={isTaskComplete}
      taskId={taskId}
    >
      {description && (
        <Text mb="2">
          <TaskContent>{description}</TaskContent>
        </Text>
      )}
      <Box
        color="white"
        pos="relative"
        rounded="md"
        overflow="hidden"
        bg="gray.900"
        ref={containerRef}
      >
        {loadingUser && (
          <CodeChallengeLoading
            pos="absolute"
            inset={0}
            height="auto"
            bg="inherit"
          >
            Loading progress
          </CodeChallengeLoading>
        )}
        <Box
          as="iframe"
          ref={frameRef}
          title={`capsule-${challenge}@${v}`}
          src={`https://capsule.apollographql.com/${challenge}/${v}`}
          w="full"
          height={height}
        />
      </Box>
      {results !== null && (
        <Stack mt="2">
          {[...resultsObj.entries()].map(([label, results]) => (
            <Result key={label} label={label} results={results} />
          ))}
        </Stack>
      )}
      {error !== null && (
        <Stack mt="2">
          <Flex align="center" fontSize="md" color="failure">
            <Box mr="1">
              <FiAlertCircle />
            </Box>
            <span>
              <TaskContent>{error}</TaskContent>
            </span>
          </Flex>
        </Stack>
      )}
      <TaskEvent taskId={taskId} isComplete={complete} />
    </TaskWrapper>
  );
};
